import { getResource, patchResource, createResource, deleteResource } from './resource';

/**
 * Fetch all room definitions with pagination
 * @param {string} token - Access token
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @param {string} sort - Sort criteria
 * @returns {Promise<Object>} Room definitions response
 */
export const getRoomDefinitions = async (token, page = 0, size = 20, sort = 'roomType,asc') => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/roomDefinitions?page=${page}&size=${size}&sort=${sort}`;
  return getResource(token, url);
};

/**
 * Get room definitions by design package
 * @param {string} token - Access token
 * @param {string} designPackageType - Design package type (GOLD, SILVER, BRONZE)
 * @returns {Promise<Object>} Room definitions response
 */
export const getRoomDefinitionsByDesignPackage = async (token, designPackageType) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/roomDefinitions/search/findByDesignPackage?designPackageType=${designPackageType}`;
  return getResource(token, url);
};

/**
 * Get a specific room definition by ID
 * @param {string} token - Access token
 * @param {string} id - Room definition ID
 * @returns {Promise<Object>} Room definition response
 */
export const getRoomDefinition = async (token, id) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/roomDefinitions/${id}`;
  return getResource(token, url);
};

/**
 * Create a new room definition
 * @param {string} token - Access token
 * @param {Object} roomDefinition - Room definition data
 * @returns {Promise<Object>} Created room definition response
 */
export const createRoomDefinition = async (token, roomDefinition) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/roomDefinitions`;
  return createResource(token, url, roomDefinition);
};

/**
 * Update a room definition (full update)
 * @param {string} token - Access token
 * @param {string} id - Room definition ID
 * @param {Object} roomDefinition - Updated room definition data
 * @returns {Promise<Object>} Updated room definition response
 */
export const updateRoomDefinition = async (token, id, roomDefinition) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/roomDefinitions/${id}`;
  // Use PATCH for updates since PUT requires all fields
  return patchResource(token, url, roomDefinition);
};

/**
 * Partially update a room definition
 * @param {string} token - Access token
 * @param {string} id - Room definition ID
 * @param {Object} updates - Partial room definition updates
 * @returns {Promise<Object>} Updated room definition response
 */
export const patchRoomDefinition = async (token, id, updates) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/roomDefinitions/${id}`;
  return patchResource(token, url, updates);
};

/**
 * Delete a room definition
 * @param {string} token - Access token
 * @param {string} id - Room definition ID
 * @returns {Promise<void>} Delete response
 */
export const deleteRoomDefinition = async (token, id) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/roomDefinitions/${id}`;
  return deleteResource(token, url);
};

/**
 * Update room definition price
 * @param {string} token - Access token
 * @param {string} id - Room definition ID
 * @param {number} price - New price
 * @param {number} oldPrice - Old price (optional)
 * @returns {Promise<Object>} Updated room definition response
 */
export const updateRoomDefinitionPrice = async (token, id, price, oldPrice = null) => {
  const updates = { price };
  if (oldPrice !== null) {
    updates.oldPrice = oldPrice;
  }
  return patchRoomDefinition(token, id, updates);
};
